import { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnBackdropClick?: boolean;
}

export default function Modal({ isOpen, onClose, title, children, size = 'md', closeOnBackdropClick = true }: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Fullscreen mode
  if (size === 'full') {
    return (
      <div
        className="fixed inset-0 bg-white flex flex-col overflow-hidden"
        style={{
          zIndex: 99999,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          height: '100dvh',
          width: '100vw',
        }}
      >
        {/* Header for fullscreen - only if title provided */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b bg-white flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        )}
        {/* Content - no padding when no title (custom layouts handle their own) */}
        <div className={clsx("flex-1 min-h-0 overflow-hidden", title ? "p-6 overflow-y-auto" : "")}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={closeOnBackdropClick ? onClose : undefined}
        />

        {/* Modal */}
        <div
          className={clsx(
            'relative bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto',
            {
              'max-w-sm': size === 'sm',
              'max-w-md': size === 'md',
              'max-w-2xl': size === 'lg',
              'max-w-4xl': size === 'xl',
            }
          )}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">{title}</h3>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Content */}
          <div className="p-7">{children}</div>
        </div>
      </div>
    </div>
  );
}
